import { Observer, observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { Portal } from 'react-portal';
import classnames from 'classnames';

import { useHotkeys, useScrollLock, useStore } from 'shared/hooks';
import { ICONS } from 'shared/constants';
import { UiIcon } from 'shared/uikit';

import './styles.scss';

type PropsType = {
    isOpened?: boolean;
    style?: React.CSSProperties;
    className?: string;
    onClose?: () => void;
    children?: React.ReactNode;
    fullHeight?: boolean;
};

export const UiPopup = observer(function UiPopup({
    isOpened = false,
    style = {},
    onClose,
    children,
    className,
    fullHeight = false,
}: PropsType) {
    const bodyRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const store = useStore(() => ({
        hasScroll: false
    }));

    useScrollLock(isOpened);

    useHotkeys(
        'escape',
        () => {
            if (isOpened) onClose?.();
        },
        { enableOnFormTags: ['input', 'select', 'textarea'] }
    );

    useEffect(() => {
        const checkHeight = () => {
            const el = contentRef.current;
            if (!el) return;
            const contentHeight = el.scrollHeight;
            const windowHeight = window.innerHeight;
            const padding = fullHeight ? 0 : 32
            store.set('hasScroll', contentHeight > windowHeight - padding);
        };

        const frame = requestAnimationFrame(checkHeight);

        const resizeObserver = new ResizeObserver(checkHeight);
        const el = contentRef.current;
        if (el) resizeObserver.observe(el);

        window.addEventListener('resize', checkHeight);

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkHeight);
        };
    }, [isOpened]);

    if (!isOpened) return null;

    return (
        <Portal>
            <Observer>
                {() => (
                    <div
                        className={classnames('ui-popup', className, {
                            'ui-popup--opened': isOpened,
                            'ui-popup--full-height': fullHeight,
                        })}
                        style={style}
                    >
                        <div className="ui-popup__overlay" onClick={onClose}/>
                        <div ref={bodyRef}
                            className={classnames('ui-popup__body', {
                                'ui-popup__body--scrollable': store.hasScroll,
                            })}
                        >
                            <button
                                type="button"
                                className="ui-popup__close"
                                onClick={onClose}
                            >
                                <UiIcon
                                    className="ui-popup__close-icon"
                                    size={24}
                                    icon={ICONS.CROSS}
                                    color="#FFFFFF"
                                />
                            </button>
                            <div
                                className={classnames('ui-popup__content', {
                                    'ui-popup__content--scrollable': store.hasScroll
                                })}
                            >
                                <div className={classnames('ui-popup__content-inner', {
                                    'ui-popup__content-inner--scrollable': store.hasScroll,
                                })} ref={contentRef}>
                                    {children}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Observer>
        </Portal>
    );
});
